import React, { useContext, useEffect, useRef, useState } from "react";
import "./styles.css";
import PATHS from "../../Routes/paths";
import { Breadcrumb, RippleButton, Switcher, Title } from "../../Components/Atoms";
import { StoreContext } from "../../Library/contexts";
import { Button, Input, Spinner } from "@chakra-ui/react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getRoutes } from "../../APIs";
import { AddIcon, ArchivedIcon, ArrowRightIcon, ChevronDownIcon, EditModelIcon, SearchIcon } from "../../Assets/images/svgs";
import { RouteSummary } from "../../Components/Organisms";
import { getISODate, stampToDate } from "../../Library/datetime";
import { DatePicker } from "../../Components/Molecules";
import { DATE_OPTIONS, DATE_RANGES } from "../../Library/constants";

const ViewRoutes = ({ archived = false, tab }) => {
  const [ routes, setRoutes ] = useState([]);
  const [ showRouteStops, setShowRouteStops ] = useState(0);
  const [ searchString, setSearchString ] = useState("");
  const [ archivedRouteCount, setArchivedRouteCount ] = useState(0);
  const [ selectedTab, setSelectedTab ] = useState(archived ? 2 : 1);
  const { userVehicles, allVehicleRoutes, setAllVehicleRoutes, lastViewState, setLastViewState } = useContext(StoreContext);
  const userVehMap = useRef({});
  const originalData = useRef([]);
  const [ searchParams, setSearchParams ] = useSearchParams();
  const [ showLoader, setShowLoader ] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  const fetchRoutes = (fetchNew) => {
    if ((!allVehicleRoutes || !allVehicleRoutes.length) || fetchNew) {
      getRoutes((err, data) => {
        if (err) console.error(err);
        else {
          let archivedRoutes = data.filter(route => route.archived);
          setAllVehicleRoutes(data);
          if (!archived) {
            let nonArchivedRoutes = data.filter(route => !route.archived);
            setRoutes(nonArchivedRoutes);
            originalData.current = nonArchivedRoutes;
          } else {
            let archivedRts = data.filter(route => route.archived)
            setRoutes(archivedRts);
            originalData.current = archivedRts;
          }
          setArchivedRouteCount(archivedRoutes.length);
        }
        setShowLoader(false);
      });
    } else {
      let archivedRoutes = allVehicleRoutes.filter(route => route.archived);
      setArchivedRouteCount(archivedRoutes.length);
      if (!archived) setRoutes(allVehicleRoutes.filter(route => !route.archived));
      else setRoutes(allVehicleRoutes.filter(route => route.archived));
      setShowLoader(false);
    }
  }

  useEffect(() => {
    if (!userVehicles || !userVehicles.length) return;

    userVehicles.forEach(vehicle => {
      userVehMap.current[vehicle.idx] = vehicle;
    });
  }, [ userVehicles ]);

  const route_stat_labels = [
    { label: "Route Distance", key: "totalDistance", dst: true },
    { label: "No. Of Stops", key: "fences", length: true },
    { label: "Vehicles Assigned", key: "vids", length: true, archiveLabel: "Archived Date", archiveKey: "archived" },
  ];

  const handleRouteSearch = (e) => {
    if (!e.target.value) {
      setSearchString("");
      setRoutes(originalData.current);
      setShowRouteStops(0);
      return;
    }
    let value = e.target.value?.toLowerCase();
    setSearchString(value);

    let filteredRoutes = originalData.current.filter(route => {
      let fenceNames = route.fences.map(fence => fence.title);
      let fenceNmIncludesVal = fenceNames.filter(name => name.toLowerCase().includes(value));
      return !!fenceNmIncludesVal.length || route.nm.toLowerCase().includes(value);
    });

    setRoutes(filteredRoutes);
    if (filteredRoutes && filteredRoutes.length) setShowRouteStops(1);
  }

  const switcherOptions = [
    { idx: 1, label: "Summary" },
    { idx: 2, label: "Routes" },
  ];

  const handleSwitcherChange = (tab) => {
    setSelectedTab(tab.idx);
  };

  useEffect(() => {
    fetchRoutes();
    if (lastViewState.path) navigate(lastViewState.path);
    if (lastViewState.tab) setSelectedTab(lastViewState.tab);
  }, []);

  useEffect(() => {
    if (searchParams.get("fromUpdate")) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("fromUpdate");
      setSearchParams(newParams);
      fetchRoutes(true);
    }
  }, [ location ]);

  useEffect(() => {
    return () => {
      setLastViewState({ tab: selectedTab });
    }
  }, [ selectedTab ]);

  return (
    <section className="panel-main view-routes">
      {!showLoader ? <>
        {routes.length ? <div className="routes-header-container">
          {!archived ? <div className="routes-switcher-container">
            <Switcher
              className={`alerts-switcher`}
              options={switcherOptions}
              selectedOption={selectedTab}
              handleChange={handleSwitcherChange}
            />
          </div> : null}
          {!archived && <DatePicker customMaxDate={getISODate(DATE_RANGES[DATE_OPTIONS.YESTERDAY].from)} />}
        </div> : null}

        {(selectedTab === switcherOptions[0].idx && !archived) ? <RouteSummary allRoutes={routes} /> : <div className="routes-page">
          <>
            {!archived ? <><div className="routes-page-header">
              <Button className="archived-routes-button" onClick={() => navigate(`${PATHS.VIEW_ROUTES}${PATHS.ARCHIVED_ROUTES}`)}>
                <ArchivedIcon />
                View Archived Routes ({archivedRouteCount})
              </Button>
              <Button className="set-route-btn" onClick={() => navigate(`${PATHS.VIEW_ROUTES}${PATHS.SET_ROUTES}?fromAlerts=${true}`)}>
                <AddIcon />
                Create new route
              </Button>
            </div>
            <div className="search-routes-container">
              <SearchIcon />
              <Input placeholder="Search Routes" onChange={handleRouteSearch} value={searchString} />
            </div></> : null}
            {routes && routes.length ? <div className="routes-list-container">
              {routes.map((route, idx) => {
                return (
                  <div key={route.idx} className="route-card">
                    <div className="route-top-row-container">
                      <div className="route-card-top-row">
                        <p>{route.nm || ""}</p>
                        {!archived ? <RippleButton onClick={() => navigate(`${PATHS.VIEW_ROUTES}${PATHS.SET_ROUTES}?fromAlerts=${true}&idx=${route.idx}&isEdit=true`)}>
                          <EditModelIcon />
                          Edit
                        </RippleButton> : null}
                      </div>
                      <div className="route-stats-row">
                        {route_stat_labels.map(stat => (
                          <div className="stat-value-box">
                            <p>{(archived && stat.archiveLabel) ? stat.archiveLabel : stat.label} :</p>
                            <p>{(archived && stat.archiveKey) ? stampToDate(route[stat.archiveKey]) : ((stat.length ? route[stat.key].length : (stat.dst ? `${route[stat.key].toFixed(2)} kms` : route[stat.key])) || 0)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="view-route-expandable-row">
                      <Button variant={"unstyled"} onClick={() => showRouteStops === (idx + 1) ? setShowRouteStops(0) : setShowRouteStops(idx+1)}>
                        View Route
                        <ChevronDownIcon className={showRouteStops === (idx + 1) ? "rotate-arrow" : "arrow-icon"} />
                      </Button>
                      <div className="show-route-stops-box">
                        {route.fences.map((point, i) => (
                          <div className={`single-route-point-box ${showRouteStops === (idx + 1) ? "expanded" : ""}`}>
                            <p>{point.title}</p>
                            {(i !== (route.fences.length - 1)) ? <ArrowRightIcon /> : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div> : <div className="empty-route-list">You haven't added any routes yet</div>}
          </>
        </div>}
      </> : <Spinner />}
    </section>
  )
}

export default ViewRoutes
